import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contestCandidateName, contestProjectTitle } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { ContestMaturationPanel } from '@/features/concours/ContestMaturationPanel';
import { ContestDocumentsPanel } from '@/features/concours/ContestDocumentsPanel';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/concours/concours.css';

export function ContestEvolutionPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [myRegs, setMyRegs] = useState<{ id: string; form_data: Record<string, string>; contest_id: string }[]>(
    [],
  );
  const [evaluations, setEvaluations] = useState<
    Record<string, { current_level: string; jury_decision: string; jury_observations: string }>
  >({});
  const [openReg, setOpenReg] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    void (async () => {
      const { data, error: err } = await kuunda
        .from('contest_registrations')
        .select('id, form_data, contest_id')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false });
      if (err) toast.error(err.message);
      else {
        const rows = data ?? [];
        setMyRegs(rows);
        if (rows[0]) setOpenReg(rows[0].id);
        const ids = rows.map((row) => row.id);
        if (ids.length > 0) {
          const { data: evalRows } = await kuunda
            .from('contest_evaluations')
            .select('registration_id, current_level, jury_decision, jury_observations')
            .in('registration_id', ids);
          setEvaluations(
            Object.fromEntries(
              (evalRows ?? []).map((row) => [
                row.registration_id,
                {
                  current_level: row.current_level ?? '',
                  jury_decision: row.jury_decision ?? '',
                  jury_observations: row.jury_observations ?? '',
                },
              ]),
            ),
          );
        } else {
          setEvaluations({});
        }
      }
    })();
  }, [profile, toast]);

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">
            Parcours en six étapes, mis à jour selon les informations communiquées par le jury.
          </p>
        </div>
        <div className="u-actions">
          <Link className="btn btn-ghost" to="/app/concours">
            Retour
          </Link>
          <Link className="btn btn-primary" to="/app/concours/inscription">
            S&apos;inscrire
          </Link>
        </div>
      </div>

      <section className="u-card">
        {myRegs.length === 0 ? (
          <p className="u-empty">
            Vous n&apos;avez pas encore de candidature. Inscrivez-vous pour suivre votre évolution auprès du
            jury.
          </p>
        ) : (
          <>
            <ul className="cx-evo-list">
              {myRegs.map((r) => (
                <li key={r.id}>
                  <div>
                    <strong>{contestProjectTitle(r.form_data) || 'Candidature'}</strong>
                    <div className="muted">{contestCandidateName(r.form_data)}</div>
                  </div>
                  <button
                    type="button"
                    className={`btn ${openReg === r.id ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setOpenReg(r.id)}
                  >
                    {openReg === r.id ? 'Affiché' : 'Voir le parcours'}
                  </button>
                </li>
              ))}
            </ul>

            {openReg ? (
              <>
                {evaluations[openReg]?.jury_decision ? (
                  <div className="u-card" style={{ marginBottom: '1rem' }}>
                    <h3 className="u-section-title">Décision du jury</h3>
                    <p>
                      <strong>{evaluations[openReg].jury_decision}</strong>
                      {evaluations[openReg].current_level ? ` · Niveau actuel : ${evaluations[openReg].current_level}` : ''}
                    </p>
                    <p className="muted" style={{ whiteSpace: 'pre-wrap' }}>
                      {evaluations[openReg].jury_observations || 'Aucune observation transmise pour le moment.'}
                    </p>
                  </div>
                ) : null}
                <h3 className="u-section-title">Parcours du jury</h3>
                <ContestMaturationPanel registrationId={openReg} readOnly />
                <div style={{ marginTop: '1.25rem' }}>
                  <h4 className="u-section-title">Documents de candidature</h4>
                  <ContestDocumentsPanel registrationId={openReg} />
                </div>
              </>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
